import cx from 'clsx';
import React from 'react';

import GameLink from '@/components/GameLink';
import { isActive } from '@/components/Header/constants';
import { ShouldPreventDefaultForClick } from '@/components/LaunchStrategiesProvider/utils';
import { GameClickProps } from '@/components/modular-grid/types';
import { MainNavItem } from '@/components/SideNavigation/types';

import styles from './MainNavigationSlot.module.scss';

interface MainNavigationSlotPropTypes {
  item: MainNavItem;
  onNavigationGameClick?: (args: {
    itemTitle: string;
    game: GameClickProps;
    sectionTitle: string;
    gamePosition: number;
  }) => void;
  onNavigationGameInfoClick?: (args: {
    itemTitle: string;
    game: GameClickProps;
    sectionTitle: string;
    gamePosition: number;
  }) => void;
  onNavigationCommandClick?: (args: {
    itemTitle: string;
    account?: string;
  }) => void;
  isHovered?: boolean;
  keyId?: number;
  pageTitle?: string;
}

const MainNavigationSlot = ({
  item,
  onNavigationGameInfoClick,
  onNavigationCommandClick,
  onNavigationGameClick,
  isHovered,
  keyId,
  pageTitle,
}: MainNavigationSlotPropTypes) => {
  const {
    state,
    title,
    game,
    url,
    route_link,
    target,
    command,
    image,
    type,
  } = item;
  const notIndexed = item.notIndexed ? 'nofollow' : '';
  const preventDefaultForGameClick =
    item.game && ShouldPreventDefaultForClick(item.game.integration);
  const preventDefaultForClick = ShouldPreventDefaultForClick();

  return (
    <div className={styles.slot} data-testid="main-nav-slot">
      <div
        className={cx(
          styles.icon,
          state === 'active' || isHovered
            ? styles.iconActive
            : styles.iconInactive
        )}
        data-testid="icon"
        style={{
          maskImage: `url(${image.source})`,
          WebkitMaskImage: `url(${image.source})`,
        }}
      ></div>
      {isActive(state) && (
        <div
          data-testid="selection-element"
          className={styles.selectionElement}
        />
      )}
      {(type === 'game_details' || type === 'game_launch') && (
        <GameLink
          className={cx(
            styles.anchorLayer,
            styles.title,
            state === 'active' && styles.titleActive
          )}
          notIndexed={item.notIndexed}
          data-testid="slot-anchor-title"
          target="_blank"
          categoryAttributes={{
            title: 'navigation',
            topCategory: pageTitle,
          }}
          dataTestId="slot-anchor-title"
          url={item.game!.launchUrl}
          integration={item.game!.integration}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            if (!game) return;
            if (preventDefaultForGameClick) e.preventDefault();
            const clickProps = {
              game: {
                ...item.game!,
                trackingTitle: game.tracking_metadata?.title || game.title,
              },
              itemTitle: item.title,
              sectionTitle: 'Side Nav',
              gamePosition: keyId || 0,
              path: game.gameDetailsPath,
            };
            if (type === 'game_details') {
              onNavigationGameInfoClick &&
                onNavigationGameInfoClick(clickProps);
            } else {
              onNavigationGameClick && onNavigationGameClick(clickProps);
            }
          }}
        >
          {title}
        </GameLink>
      )}
      {type === 'command' && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          className={cx(
            styles.anchorLayer,
            styles.title,
            state === 'active' && styles.titleActive
          )}
          data-testid="slot-anchor-title"
          rel={`noopener noreferrer ${notIndexed}`}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            if (command === 'usabilla') {
              e.preventDefault();
              window.usabilla_live && window.usabilla_live('click');
              return;
            }
            if (preventDefaultForClick) e.preventDefault();
            onNavigationCommandClick &&
              onNavigationCommandClick({
                itemTitle: title,
                account: command,
              });
          }}
        >
          {title}
        </a>
      )}
      {type === 'external' && (
        <a
          className={cx(
            styles.anchorLayer,
            styles.title,
            state === 'active' && styles.titleActive
          )}
          data-testid="slot-anchor-title"
          rel={`noopener noreferrer`}
          href={url || ''}
          target={target || ''}
        >
          {title}
        </a>
      )}
      {(!type ||
        !['external', 'command', 'game_launch', 'game_details'].includes(
          type
        )) && (
        <a
          className={cx(
            styles.anchorLayer,
            styles.title,
            state === 'active' && styles.titleActive
          )}
          data-testid="slot-anchor-title"
          target={target || '_self'}
          rel={`noopener noreferrer ${notIndexed}`}
          href={route_link || ''}
        >
          {title}
        </a>
      )}
    </div>
  );
};

export default MainNavigationSlot;
